import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { useNarrow } from '../../../ui/index.js';
import { useAuthStore, hasPermission } from '../../../stores/auth.js';
import { useRequestOpenStore } from '../../../stores/index.js';
import { useRequestsStore } from '../store.js';
import { useRequestChatUnreadSummaries, useRequestsInfinite } from '../queries.js';
import { wireRequestToView } from '../adapter.js';
import {
  detectStatusFilterRole,
  getDefaultRequestListStatuses,
  isLogistStatusFilterRole,
  type RequestFilters,
} from '../filters.js';
import { countActiveAdvancedFilters, useRequestsFilters } from '../useRequestsFilters.js';
import { detectRoleKind } from '../role.js';
import type { RequestView, UserRef } from '../types.js';
import { STATUS_LABEL_KEYS } from './RequestStatusPill.js';
import { RequestDrawer } from './RequestDrawer.js';
import { DraftDrawer } from './DraftDrawer.js';
import { useRequestUrlState } from '../useRequestUrlState.js';
import { useOpenRequestRow } from '../useOpenRequestRow.js';
import { RequestsAdvancedFilterPanel } from './RequestsAdvancedFilterPanel.js';
import { RequestsListRoleTable } from './RequestsListRoleTable.js';
import { RequestsListPageHeader } from './RequestsListPageHeader.js';
import { RequestsListFilterStrip } from './RequestsListFilterStrip.js';
import { useRequestChipCounts } from './useRequestChipCounts.js';
import { useRequestNotificationLookup } from './useRequestNotificationLookup.js';
import { STATUS_FILTER_IDS_FULL, STATUS_FILTER_IDS_LOGIST } from './requestsListFilterIds.js';
import { SEARCH_MAX_CHARS, SLA_FILTER_IDS, KIND_FILTER_IDS } from './requestsListFilterIds.js';
import { useRequestsBatchExport } from '../selection/useRequestSelection.js';
import { RequestsBatchExportDialog } from './RequestsExportDialog.js';

export function RequestsListPage() {
  const { t } = useTranslation(['requests', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const role = detectRoleKind(user);
  // INVARIANT (#1159 PR5): hidden-status filter role is its own axis
  //   because `RoleKind` only knows about China (no `logist_ru`). The
  //   `/china-rates` route gates logist_ru out via permissions, but
  //   `STATUS_FILTER_IDS_LOGIST` is shared with `/russia-rates` via
  //   the same helper, so the role tag here is the source of truth.
  const statusFilterRole = detectStatusFilterRole(user);
  const isLogistFilterActor = isLogistStatusFilterRole(statusFilterRole);
  const STATUS_FILTER_IDS = isLogistFilterActor ? STATUS_FILTER_IDS_LOGIST : STATUS_FILTER_IDS_FULL;
  // INVARIANT: backend `/requests` returns the actor-scoped row set
  // (`apps/backend/src/modules/requests/visibility.ts`). The page does not
  // re-filter — chip counts and the visible list both describe the wire
  // response 1:1 so the UI can never disagree with the authoritative scope.
  const narrow = useNarrow();

  const { selectedId, select, draft, openDraft } = useRequestsStore(
    useShallow((s) => ({
      selectedId: s.selectedId,
      select: s.select,
      draft: s.draft,
      openDraft: s.openDraft,
    })),
  );
  const pendingFreshDraft = useRequestOpenStore((s) => s.pendingFreshDraft);
  const consumePendingFreshDraft = useRequestOpenStore((s) => s.consumePendingFreshDraft);
  const urlState = useRequestUrlState();
  // INVARIANT: URL is the canonical source of `openId` on `/requests`.
  // The Zustand `selectedId` is a passive mirror — kept in sync by this
  // single page-level effect so legacy consumers (list highlighting,
  // realtime row matching) keep reading the store. URL writes flow
  // exclusively through `useRequestUrlState`; the store's `select` stays
  // a passive setter and never calls router APIs.
  useEffect(() => {
    if (urlState.currentOpenId !== selectedId) {
      select(urlState.currentOpenId);
    }
  }, [urlState.currentOpenId, selectedId, select]);

  // WHY (#1753): a fresh China draft requested from a CRM lead/deal arrives
  //   here via the neutral requestOpen store. Consume it once on arrival and
  //   open the pre-bound draft on the requests store — same scratchpad path
  //   as the in-page "+" buttons, just triggered cross-feature.
  useEffect(() => {
    if (!pendingFreshDraft || !user) return;
    consumePendingFreshDraft();
    const me: UserRef = { id: user.id, name: user.name, role: detectRoleKind(user) };
    openDraft(me, pendingFreshDraft);
  }, [pendingFreshDraft, user, openDraft, consumePendingFreshDraft]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const [search, setSearch] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // WHY (#1507): the filter-chip group is collapsed by default behind a
  //   header toggle; the colour + count badge signal an active filter
  //   without expanding. The id wires the toggle's `aria-controls` to
  //   the always-mounted wrapper in `RequestsListFilterStrip`.
  const [chipsOpen, setChipsOpen] = useState(false);
  const chipsGroupId = 'requests-chip-group';

  // WHY: picker visibility honours the AC "users.manage OR
  //   requests.scope.all". The picker now sources its options from the
  //   request-domain endpoint (`GET /api/requests/manager-options`,
  //   gated by `requests.read` and naturally scoped by visibility),
  //   so a holder of `requests.scope.all` without `users.manage` no
  //   longer trips the 403-loop the previous `useUsersList` source
  //   suffered (#421). Any caller landing here already holds
  //   `requests.read` (the page mounts only when the requests route
  //   guard accepts them), so the new endpoint is reachable for both
  //   paths of the OR.
  const canPickManager =
    hasPermission(permissions, 'users.manage') || hasPermission(permissions, 'requests.scope.all');

  const {
    selectedStatuses,
    toggleStatus: toggleStatusFilter,
    selectedSlaStates,
    toggleSlaState: toggleSlaFilter,
    unreadOnly,
    toggleUnread: toggleUnreadFilter,
    selectedKind,
    toggleKind: toggleKindFilter,
    advanced,
    setAdvanced,
    clearAdvanced,
    wireFilters: persistedWireFilters,
  } = useRequestsFilters({ canPickManager });

  // INVARIANT: every visible filter MUST live on the wire, otherwise
  //   pagination strands matches past page 1. The hook produces the
  //   persisted shape (no `q`) and a `null` sentinel while manager-options
  //   are validating; this memo splices the local search `q` on top so
  //   the live query key is identical to the hook output plus an optional
  //   `q`. Returning `null` keeps `useRequestsInfinite` disabled and the
  //   list area in its loading state until validation settles.
  const effectiveWireFilters = useMemo<Partial<RequestFilters> | null>(() => {
    if (persistedWireFilters === null) return null;
    // INVARIANT: the wire schema bounds `q` at `[1, 200]` characters
    //   (`packages/shared/src/schemas/requests.ts`). The native input
    //   already caps typing/paste at SEARCH_MAX_CHARS, but a clamp here
    //   protects the wire if the value enters the state via any path
    //   that bypasses the input. Trim runs after the clamp so trailing
    //   whitespace inside the 200-char window does not push the wire
    //   over the limit.
    const trimmed = search.slice(0, SEARCH_MAX_CHARS).trim();
    return trimmed ? { ...persistedWireFilters, q: trimmed } : persistedWireFilters;
  }, [persistedWireFilters, search]);

  const requestsQuery = useRequestsInfinite(
    effectiveWireFilters ?? { status: [...getDefaultRequestListStatuses(statusFilterRole)] },
    { enabled: effectiveWireFilters !== null },
  );
  const requests = useMemo<RequestView[]>(() => {
    const pages = requestsQuery.data?.pages ?? [];
    return pages.flatMap((page) => page.items.map((wire) => wireRequestToView(wire)));
  }, [requestsQuery.data?.pages]);

  // WHY (#570): unread-summary covers every LOADED id, not a 200-row
  //   slice. The wire endpoint caps each call at
  //   `REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS` (200), so the chunked hook
  //   splits the id-set into ≤200-sized batches under stable per-batch
  //   query keys and merges results into a single map. Realtime
  //   invalidation via `chatUnreadSummaryPrefix()` re-fetches every
  //   chunk in one shot.
  const loadedRequestIds = useMemo(() => requests.map((r) => r.id), [requests]);
  const { data: unreadById } = useRequestChatUnreadSummaries(loadedRequestIds);
  const unreadFor = useCallback(
    (id: string): { unreadCount: number; isMarkedUnread: boolean } =>
      unreadById.get(id) ?? { unreadCount: 0, isMarkedUnread: false },
    [unreadById],
  );

  // INVARIANT (#1072): one notifications query backs the bell + every
  //   row dot via the shared `NOTIFICATIONS_LIST_QK`. The lookup hook
  //   wraps the shared selector so this page only depends on
  //   `(requestId) -> number`.
  const notificationCountFor = useRequestNotificationLookup();

  const filters = useMemo(
    () =>
      STATUS_FILTER_IDS.map((id) =>
        id === 'all'
          ? { id, label: t('requests:list.all') }
          : { id, label: t(STATUS_LABEL_KEYS[id]) },
      ),
    [t, STATUS_FILTER_IDS],
  );
  const slaFilters = useMemo(
    () => [
      { id: 'hot' as const, label: t('requests:filters.slaHot') },
      { id: 'overdue' as const, label: t('requests:filters.slaOverdue') },
    ],
    [t],
  );
  const kindFilters = useMemo(
    () => [
      { id: 'STANDARD' as const, label: t('requests:filters.kindStandard') },
      { id: 'CONTAINER' as const, label: t('requests:filters.kindContainer') },
      { id: 'SIMPLE' as const, label: t('requests:filters.kindSimple') },
    ],
    [t],
  );

  const counts = useRequestChipCounts({
    requests,
    selectedStatuses,
    selectedSlaStates,
    selectedKind,
    unreadOnly,
    unreadById,
    now,
    statusFilterIds: STATUS_FILTER_IDS,
    slaFilterIds: SLA_FILTER_IDS,
    kindFilterIds: KIND_FILTER_IDS,
    statusFilterRole,
  });

  // WHY: pages arrive in cursor order (`createdAt DESC, id DESC`), so any
  // client-side reorder by another field would let a row created earlier
  // but updated recently hide on page N then jump above already-rendered
  // rows on the next load-more — defeating the implicit ordering of the
  // cursor. Filter only; trust the server order.
  // INVARIANT: every visible filter is on the wire (status, search,
  //   date, weight, volume, manager). The defense-in-depth client-side
  //   pass below would only diverge from the server result if the
  //   backend ignored a filter — in which case rendering the rejected
  //   row would mask the contract break. Hiding it locally keeps the
  //   wire boundary strict at the cost of a single re-filter pass.
  // INVARIANT (#630): while `effectiveWireFilters === null` (manager-options
  //   validation pending for a hydrated managerId set), suppress every
  //   cached row — the previous filter set's data must NOT flash behind
  //   the hydrated chips.
  const visible = useMemo(() => {
    if (effectiveWireFilters === null) return [];
    const allowed: readonly RequestStatus[] =
      selectedStatuses.length === 0
        ? getDefaultRequestListStatuses(statusFilterRole)
        : selectedStatuses;
    return requests.filter((r) => allowed.includes(r.status));
  }, [requests, selectedStatuses, effectiveWireFilters, statusFilterRole]);

  const isManagerValidating = effectiveWireFilters === null;
  const batchExport = useRequestsBatchExport({
    visible,
    filters: effectiveWireFilters,
  });

  // INVARIANT: scratchpad model — clicking "+" opens an unpersisted local
  // draft. It only becomes a wire row when the user clicks Send (which
  // chains createRequest + transition('send') in DraftEditor). This avoids
  // creating a wire row with placeholder values that would either fail
  // schema (`route.from/to: min(1)`) or pollute the list.
  const onCreate = (): void => {
    if (!user) return;
    const me: UserRef = { id: user.id, name: user.name, role: detectRoleKind(user) };
    openDraft(me);
  };

  // WHY (#701): companion to `onCreate` — opens the editor in CONTAINER
  //   mode (TransportType picker visible, boxes section hidden, per-item
  //   weight/volume optional).
  const onCreateContainer = (): void => {
    if (!user) return;
    const me: UserRef = { id: user.id, name: user.name, role: detectRoleKind(user) };
    openDraft(me, { kind: 'CONTAINER' });
  };

  // WHY (#1306): companion to `onCreate` — opens the editor in SIMPLE
  //   mode (insurance, the separate roads/packaging card, and the boxes
  //   section hidden; roads/packaging move inline under destinations).
  const onCreateSimple = (): void => {
    if (!user) return;
    const me: UserRef = { id: user.id, name: user.name, role: detectRoleKind(user) };
    openDraft(me, { kind: 'SIMPLE' });
  };

  const isChipActive = (id: 'all' | RequestStatus): boolean =>
    id === 'all' ? selectedStatuses.length === 0 : selectedStatuses.includes(id);

  const isSlaChipActive = (id: 'hot' | 'overdue'): boolean => selectedSlaStates.includes(id);

  const openRow = useOpenRequestRow();

  // WHY (#1507): badge count surfaces how many chip filters are active
  //   while the group is collapsed.
  const activeChipCount =
    selectedStatuses.length +
    selectedSlaStates.length +
    (unreadOnly ? 1 : 0) +
    (selectedKind ? 1 : 0);

  // WHY (#1526): search and the four advanced groups all live behind the
  //   collapsed "Filters" panel, so its toggle shows the combined hidden
  //   count without changing statuses-chip behavior.
  const advancedActiveCount =
    (search.trim().length > 0 ? 1 : 0) + countActiveAdvancedFilters(advanced);

  return (
    <div
      data-testid="requests-list-page"
      style={{
        position: 'relative',
        margin: -16,
        height: 'calc(100% + 32px)',
        width: 'calc(100% + 32px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-0)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <RequestsListPageHeader
        role={role}
        permissions={permissions}
        narrow={narrow}
        advancedOpen={advancedOpen}
        advancedActiveCount={advancedActiveCount}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
        chipsOpen={chipsOpen}
        onToggleChips={() => setChipsOpen((v) => !v)}
        activeChipCount={activeChipCount}
        chipsGroupId={chipsGroupId}
        onCreateSimple={onCreateSimple}
        onCreate={onCreate}
        onCreateContainer={onCreateContainer}
        batchExport={batchExport}
      />

      <RequestsListFilterStrip
        chipsOpen={chipsOpen}
        chipsGroupId={chipsGroupId}
        filters={filters}
        slaFilters={slaFilters}
        kindFilters={kindFilters}
        counts={counts}
        isStatusActive={isChipActive}
        isSlaActive={isSlaChipActive}
        selectedKind={selectedKind}
        unreadOnly={unreadOnly}
        unreadLabel={t('requests:filters.unread')}
        onToggleStatus={toggleStatusFilter}
        onToggleSla={toggleSlaFilter}
        onToggleUnread={toggleUnreadFilter}
        onToggleKind={toggleKindFilter}
      />

      {advancedOpen && (
        <RequestsAdvancedFilterPanel
          state={advanced}
          onChange={setAdvanced}
          onClear={clearAdvanced}
          canPickManager={canPickManager}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('requests:list.searchPlaceholder')}
          searchMaxChars={SEARCH_MAX_CHARS}
        />
      )}

      <RequestsListRoleTable
        role={role}
        visible={visible}
        now={now}
        narrow={narrow}
        selectedId={selectedId}
        isManagerValidating={isManagerValidating}
        isError={requestsQuery.isError}
        isInitialLoading={
          !isManagerValidating && requestsQuery.isFetching && requestsQuery.data === undefined
        }
        hasNextPage={requestsQuery.hasNextPage}
        isFetchingNextPage={requestsQuery.isFetchingNextPage}
        onOpenRow={openRow}
        onLoadMore={() => {
          void requestsQuery.fetchNextPage();
        }}
        unreadFor={unreadFor}
        notificationCountFor={notificationCountFor}
        batchExport={batchExport}
      />

      <RequestsBatchExportDialog controller={batchExport} />

      {/* Drawer overlay — draft scratchpad takes precedence over a wire row */}
      {draft && <DraftDrawer narrow={narrow} me={draft.manager} />}
      {!draft && urlState.currentOpenId != null && (
        <RequestDrawer
          requestId={urlState.currentOpenId}
          narrow={narrow}
          onClose={() => urlState.closeRequest()}
        />
      )}
    </div>
  );
}
